import type { Json } from "./json";
import type { Domain, ViewMode } from "./domain";

// A command is a typed intent. Components dispatch commands; the CommandBus
// resolves exactly one handler. Mirrors the backend kernel's Command.
export interface Command<TType extends string = string, TPayload = unknown> {
    readonly type: TType;
    readonly payload: TPayload;
}

// The platform (domain-agnostic) command set. Domains register additional
// command types at runtime via CommandBus.register — never edit this union for a domain.
export interface PlatformCommandPayloads {
    NAVIGATE: { to: string };
    SWITCH_ORG: { organizationId: string };
    SWITCH_APP: { appId: string };
    OPEN_ACTION: { actionKey: string; viewMode?: ViewMode };
    /** Open an `ir.action` inside a Radix Dialog overlay (no URL change).
        Routed independently from OPEN_ACTION so the dispatcher's page-
        navigation logic and the dialog-stack push logic stay separate.
        Use this when the action's `target` field is `"dialog"`. */
    OPEN_ACTION_DIALOG: {
        actionKey: string;
        recordRef?: string;
        viewMode?: "form";
    };
    OPEN_RECORD: { modelKey?: string; recordRef: string; fromField?: string };
    CREATE_RECORD: { modelKey: string };
    SAVE_RECORD: Record<string, never>;
    DELETE_RECORD: { recordRefs: string[] };
    RUN_BUTTON: {
        actionSlug: string;
        recordUuid: string;
        commandName: string;
        // `commandType` selects which engine the dispatcher routes through.
        // Optional — omitted means the default (service-API) engine. See
        // `core/command/engineRegistry.ts` for the chain-of-resolution
        // semantics: the named engine is probed via canHandle(), with a
        // guaranteed fallback to default.
        commandType?: string | null;
        // Human label for the button — surfaced in the success toast that
        // fires on completion ("<label> done") so the user knows the action
        // committed without having to read the URL or button state. Views
        // pass the same label they render. Falls back to `commandName` when
        // omitted.
        label?: string | null;
        context?: Json;
    };
    // Emitted when a cross-user mutation (`web.client.command.done` SSE event)
    // changes a record the current user is viewing. The NotificationCenter
    // dispatches this so the open form / list refetches in place and the
    // view can surface a discreet "Updated by …" inline banner. `byUser` is
    // the principal-id of the other user, when known.
    REFETCH_RECORD: { modelKey?: string; recordUuid: string; byUser?: string };
    RUN_REPORT: { actionKey: string; recordRefs: string[] };
    RUN_SEARCH: { domain?: Domain; groupby?: string[]; order?: string };
    SET_PREFERENCE: { key: string; value: Json };
    TOGGLE_NOTIFICATIONS: Record<string, never>;
    TOGGLE_ASSISTANT: Record<string, never>;
}

export type PlatformCommandType = keyof PlatformCommandPayloads;

export type PlatformCommand = {
    [K in PlatformCommandType]: Command<K, PlatformCommandPayloads[K]>;
}[PlatformCommandType];

// Typed factory for platform commands — `command("OPEN_RECORD", { recordRef })`.
export function command<K extends PlatformCommandType>(
    type: K,
    payload: PlatformCommandPayloads[K],
): Command<K, PlatformCommandPayloads[K]> {
    return { type, payload };
}

// Handler captures its own dependencies (services, navigation, emit) at registration
// time — the bus stays dependency-free and therefore framework-agnostic.
export type CommandHandler = (command: Command) => unknown | Promise<unknown>;
export type CommandNext = () => Promise<unknown>;
export type CommandMiddleware = (command: Command, next: CommandNext) => unknown | Promise<unknown>;
